/**
 * Mỹ phẩm bán trong buổi chụp (QĐ 0260, CEO chốt 29/09/2026).
 *
 * Mục mỹ phẩm do kho GNW làm chủ, mã mục luôn là `gnw_<mã hàng>` — máy chủ giữ
 * riêng tiền tố này (tạo tay mục thường mang `gnw_` bị từ chối), nên nhận diện
 * theo mã là chắc chắn, không cần tải bảng giá.
 */

import type { Surcharge } from "./api";

/** Tiền tố mã mục hàng kho GNW (surchargecatalog.GNWItemID bên máy chủ). */
const GNW_ITEM_PREFIX = "gnw_";

/** Dòng phát sinh này có phải mỹ phẩm kho GNW không. */
export function isCosmeticLine(s: { catalogId?: string | null }): boolean {
  return (s.catalogId ?? "").startsWith(GNW_ITEM_PREFIX);
}

/** Lựa chọn khi xoá dòng mỹ phẩm đã giao (CEO 6a) — tham số `disposition`. */
export type CosmeticDisposition = "sealed" | "opened";

/**
 * Rào tự xoá của chủ dòng (spec §M2): trong 10 phút kể từ lúc thêm. Chỉ để
 * hộp xoá nói trước "sẽ gửi yêu cầu xoá" — máy chủ vẫn là bên quyết (đơn đã
 * thu tiền sau lúc thêm cũng ra yêu cầu, màn không biết điều đó).
 */
export const SELF_DELETE_WINDOW_MS = 10 * 60 * 1000;

/** Dòng đang xoá + lời đoán "sẽ thành yêu cầu xoá" tính lúc bấm nút. */
export interface CosmeticDeleteTarget {
  line: Surcharge;
  asRequest: boolean;
}

/**
 * Dựng mục tiêu xoá — GỌI TRONG sự kiện bấm (đọc đồng hồ, không được gọi lúc
 * vẽ). Người giữ quyền xoá mọi dòng luôn xoá thẳng.
 */
export function cosmeticDeleteTarget(
  line: Surcharge,
  canDeleteAny: boolean,
  now: number,
): CosmeticDeleteTarget {
  const age = now - new Date(line.addedAt).getTime();
  return { line, asRequest: !canDeleteAny && age > SELF_DELETE_WINDOW_MS };
}

/**
 * Mã máy-đọc máy chủ gắn cuối câu 409 khi huỷ đơn còn dòng mỹ phẩm đã giao
 * (order.CodeCosmeticLinesLive) — cùng khuôn `sale_line_not_assigned`.
 */
export const COSMETIC_LINES_LIVE = "cosmetic_lines_live";

/** Câu hiện cho người dùng thay cho câu máy chủ kèm mã máy. */
export const COSMETIC_LINES_LIVE_MESSAGE =
  "Đơn còn mỹ phẩm đã đưa khách — xoá từng dòng mỹ phẩm (chọn còn nguyên hộp / đã mở) rồi huỷ đơn lại";

/** Lỗi huỷ đơn bị chặn vì còn dòng mỹ phẩm — màn bắt riêng để mở hộp liệt kê. */
export class CosmeticLinesLiveError extends Error {
  constructor() {
    super(COSMETIC_LINES_LIVE_MESSAGE);
    this.name = "CosmeticLinesLiveError";
  }
}

/** id của khối Phát sinh trên đơn — hộp chặn huỷ cuộn tới đây. */
export function cosmeticSectionId(orderId: string): string {
  return `phat-sinh-${orderId}`;
}
